import styled, { keyframes } from 'styled-components';

// Backdrop overlay
export const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.5)'};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
  backdrop-filter: blur(4px);
`;

// Modal slide-in animation
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Modal container - Using Card styling from Style Guide
export const ModalContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 540px;
  border-radius: 12px;
  box-shadow: rgba(0, 0, 0, 0.3) 0px 20px 60px;
  animation: ${slideIn} 0.3s ease-out;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(30, 41, 59, 1)'
      : 'rgba(255, 255, 255, 1)'};
  color: ${(props) => props.theme.colors.text.primary};
  border: 1px solid ${(props) => props.theme.colors.card.border};

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

// Close button
export const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  z-index: 10;
  color: ${(props) => props.theme.colors.text.secondary};

  &:hover {
    background: ${(props) => props.theme.colors.interactive.hoverBackground};
    color: ${(props) => props.theme.colors.text.primary};
  }

  &:focus {
    outline: 2px solid ${(props) => props.theme.colors.primary};
    outline-offset: 2px;
  }
`;

// Modal content
export const ModalContent = styled.div`
  padding: 32px;

  @media (max-width: 768px) {
    padding: 24px;
  }
`;

// Badge - from Style Guide
export const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 16px;
  background: ${(props) => props.theme.colors.primaryOpacity(0.1)};
  color: ${(props) => props.theme.colors.primary};
`;

// Header section
export const ModalHeader = styled.div`
  margin-bottom: 24px;
`;

// Title with accent bar (Variation 3)
export const ModalTitle = styled.h3`
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 12px;
  line-height: 1.2;
  display: flex;
  align-items: center;
  gap: 12px;

  /* Accent bar */
  &::before {
    content: '';
    width: 4px;
    height: 32px;
    background: ${(props) => props.theme.colors.primary};
    border-radius: 2px;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    font-size: 24px;

    &::before {
      height: 28px;
    }
  }
`;

// Description text
export const ModalDescription = styled.p`
  font-size: 15px;
  line-height: 1.7;
  margin-bottom: 24px;
  color: ${(props) => props.theme.colors.text.secondary};
`;

// Features list
export const FeaturesList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  margin-bottom: 28px;
`;

// Individual feature item
export const FeatureItem = styled.li`
  padding: 14px 0;
  padding-left: 32px;
  position: relative;
  font-size: 15px;
  line-height: 1.6;
  color: ${(props) => props.theme.colors.text.primary};

  /* Checkmark circle - using primary color */
  &::before {
    content: '✓';
    position: absolute;
    left: 0;
    top: 16px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${(props) => props.theme.colors.primary};
    color: white;
    font-size: 12px;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

// Modal footer
export const ModalFooter = styled.div`
  padding: 0 32px 32px;

  @media (max-width: 768px) {
    padding: 0 24px 24px;
  }
`;

// Button - ButtonV2 default variant styling
export const GotItButton = styled.button`
  width: 100%;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  color: white;
  background: ${(props) => props.theme.colors.primary};

  &:hover {
    background: #e03e0c;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px ${(props) => props.theme.colors.primaryOpacity(0.3)};
  }

  &:active {
    transform: translateY(0);
  }

  &:focus {
    outline: 2px solid ${(props) => props.theme.colors.primary};
    outline-offset: 2px;
  }
`;
